import React, { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { ItemCard } from '../../../ui/ItemCard';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { residenceItemsQuery } from '../../../../api/queries';
import {
  ResidenceItemsContainer,
  ResidenceItemsCount,
  ResidenceItemsDescription,
  ResidenceItemsHead,
  ResidenceItemsList,
  ResidenceItemsTitle
} from './styled';
import { ItemSort, ResidenceItemsQuery } from '../../../../graphql/graphql';
import { ResidenceRoomsFilter } from './ResidenceRoomsFilter';
import { withEndlessScroll } from '../../../HOC/endlessScroll';
import useScreenSize from '../../../../hooks/useScreenSize';
import { ItemsPlaceholder } from '../../../Placeholders/ItemsPlaceholder';
import { useClient } from '../../../../hooks/useClient';
import { AgencyFilterResultsList } from '../../../Agency/AgencyFilter/AgencyFilterResultsList';
import { SortingPlaceholder } from '../../../Residences/ResidencesSorting/styled';

const ResidenceSortingFilter = dynamic(
  () =>
    import('./ResidenceSortingFilter').then(mod => mod.ResidenceSortingFilter),
  { ssr: false, loading: SortingPlaceholder }
);

export interface ItemsSectionProps {
  residenceId?: string;
  itemsRooms?: number[] | null;
  name?: string;
  itemsCount: number;
  residenceItems: ResidenceItemsQuery['itemsConnection']['edges'];
}

const ALL_FILTER_VALUE = 'all';

const EndlessItems = withEndlessScroll(AgencyFilterResultsList);
const itemsListRef = React.createRef();

export const ItemsSection: React.FC<ItemsSectionProps> = memo(
  ({ residenceId, itemsRooms, name, itemsCount, residenceItems }) => {
    const { t } = useTranslation();
    const { isBookmarked } = useBookmarks();
    const { isSmall, isLarge } = useScreenSize();
    const isClient = useClient();

    const [currentRooms, setCurrentRooms] = useState<string | number>(
      ALL_FILTER_VALUE
    );
    const [sortingState, setSortingState] = useState(ItemSort.BumpedAtDesc);
    const [isFirstRender, setIsFirstRender] = useState(true);

    const initialLimit = useMemo(() => {
      switch (true) {
        case isLarge || !isClient:
          return 15;
        case isSmall:
          return 6;
        default:
          return 9;
      }
    }, [isSmall, isLarge, isClient]);

    const [visibleCount, setVisibleCount] = useState(initialLimit);

    useEffect(() => {
      setVisibleCount(initialLimit);
      setIsFirstRender(true);
    }, [initialLimit]);

    useEffect(() => {
      setVisibleCount(initialLimit);
      setIsFirstRender(true);
    }, [sortingState, currentRooms, initialLimit]);

    const { data, loading, fetchMore } = useQuery<ResidenceItemsQuery>(
      residenceItemsQuery,
      {
        variables: {
          first: initialLimit,
          sort: sortingState,
          filter: {
            residenceId,
            rooms: currentRooms === ALL_FILTER_VALUE ? '' : String(currentRooms)
          }
        },
        notifyOnNetworkStatusChange: true
      }
    );

    const itemsConnection = data?.itemsConnection;
    const pageInfo = itemsConnection?.pageInfo;
    const items = itemsConnection?.edges || [];

    useEffect(() => {
      if (!loading && !pageInfo?.hasNextPage) {
        setIsFirstRender(false);
      }
    }, [loading, pageInfo?.hasNextPage]);

    const handleLoadMore = async () => {
      if (!fetchMore) return;

      if (isFirstRender) {
        setIsFirstRender(false);
      }

      if (visibleCount < items.length) {
        setVisibleCount(prev => Math.min(prev + initialLimit, items.length));
        return;
      }

      if (!pageInfo?.hasNextPage) return;

      const { data: fetchMoreData } = await fetchMore({
        variables: {
          cursor: pageInfo.endCursor,
          first: initialLimit
        }
      });

      const newEdges = fetchMoreData?.itemsConnection?.edges ?? [];

      if (newEdges.length) {
        setVisibleCount(prev =>
          Math.min(prev + initialLimit, items.length + newEdges.length)
        );
      }
    };

    const visibleItemsCount =
      currentRooms === ALL_FILTER_VALUE
        ? itemsCount
        : itemsConnection?.totalCount;

    const visibleItems = items.slice(0, visibleCount);

    const itemsList =
      visibleItems?.map(({ node }) => {
        if (!node) return null;
        return (
          <ItemCard
            stat="residence-item"
            key={node.id}
            {...node}
            inBookmarks={isBookmarked(node.id)}
          />
        );
      }) || [];

    const placeholderLength = useMemo(() => {
      switch (true) {
        case isLarge:
          return 5;
        case isSmall:
          return 2;
        default:
          return 3;
      }
    }, [isSmall, isLarge]);

    return (
      <ResidenceItemsContainer
        id="residence-items"
        data-cy="residence-items__container"
      >
        <ResidenceItemsHead>
          <ResidenceItemsDescription>
            <ResidenceItemsTitle tag="title_3_new">
              {t('residences.items_title', { residence_name: name })}
            </ResidenceItemsTitle>
            <ResidenceItemsCount
              tag="body_2"
              data-cy="residence-items-description"
            >
              {t('residences.items_count', { count: visibleItemsCount })}
            </ResidenceItemsCount>
          </ResidenceItemsDescription>

          <ResidenceSortingFilter
            currentSortValue={sortingState}
            setSortingState={setSortingState}
          />
        </ResidenceItemsHead>

        {!!itemsRooms?.length && (
          <ResidenceRoomsFilter
            itemsRooms={itemsRooms}
            currentValue={currentRooms}
            stat="residence-items-filter"
            onClick={value => {
              setCurrentRooms(value);
            }}
          />
        )}
        {!items?.length && loading && (
          <ItemsPlaceholder isCardsLayout noHeader length={placeholderLength} />
        )}
        <ResidenceItemsList
          data-cy="residence-items"
          $isFirstRender={isFirstRender}
        >
          <EndlessItems
            ref={itemsListRef}
            items={itemsList}
            loading={loading}
            isStopLoad={!pageInfo?.hasNextPage && visibleCount >= items.length}
            onLoadMore={handleLoadMore}
          >
            {itemsList}
          </EndlessItems>
        </ResidenceItemsList>
      </ResidenceItemsContainer>
    );
  }
);

ItemsSection.displayName = `ItemsSection`;
